import { useCallback, useEffect, useState } from 'react';
import type { Exportacion, FichaTransferencia, ListaTransferencias, Resumen } from '../../../api/ops/pagos';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Chip } from '../../../componentes/Chip';
import { Contexto } from '../../../componentes/Contexto';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { TarjetaCifra } from '../../../componentes/TarjetaCifra';
import { Ventana } from '../../../componentes/Ventana';
import { rolesDe } from '../../../logica/acceso';
import { cifraDinero, dinero, simboloMoneda } from '../../../logica/dinero';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { diaMes, hoyLocal } from '../../../logica/fechasCortas';
import { navegador } from '../../../logica/navegador';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { AYUDA, CONCEPTO, ESTADO_TRANSFERENCIA, FORMA_PAGO, PERMISOS, textoDeError } from '../textos';

/**
 * Conciliación (4.8): el periodo a la vista (RV Contexto, un mes) y las cifras de `getPaymentsSummary` con
 * `compare=previous` (G290): Cobrado, Devuelto, En disputa, Comisiones (de Stripe y de plataforma, sin sumarlas) y Neto
 * (`net` de la API), cada una con la del periodo anterior; sin minigráfico. Por forma de pago y por concepto en barras;
 * transferencias al banco con sus movimientos (`gross`, `fee`, `net` de la API) y «Exportar CSV». El Socio solo ve las
 * cifras (`pagos.resumen.ver`); las transferencias y la exportación piden su permiso. Nada del ERP (G74).
 */
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function meses(ahora: Date): { valor: string; texto: string }[] {
    return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
        const mes = MESES[d.getMonth()] ?? '';
        return { valor: `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`, texto: `${mes.charAt(0).toLocaleUpperCase('es')}${mes.slice(1)} de ${String(d.getFullYear())}` };
    });
}

function rango(mes: string, ahora: Date): { desde: string; hasta: string } {
    const [a = '2026', m = '01'] = mes.split('-');
    const fin = new Date(Number(a), Number(m), 0);
    const hoy = hoyLocal(ahora);
    const ultimo = hoyLocal(fin);
    return { desde: `${a}-${m}-01`, hasta: ultimo < hoy ? ultimo : hoy };
}

const tramo = (r: { from: string; to: string }) => `${diaMes(r.from)}–${diaMes(r.to)}`;

export function Conciliacion(props: PropsPantalla) {
    const { ops, negocio, movil, membresia, nombreNegocio, navegar, ruta } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const ver = permisos.puede(PERMISOS.resumen);
    const detalle = permisos.puede(PERMISOS.pagosVer);
    const exportar = permisos.puede(PERMISOS.exportar);
    const opciones = meses(new Date());
    const [mes, setMes] = useState(opciones[0]?.valor ?? '');
    const [resumen, setResumen] = useState<{ mes: string; datos: Resumen | null; error: unknown } | null>(null);
    const [transferenciasLeidas, setTransferencias] = useState<{ vista: string; lista: ListaTransferencias } | null>(null);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [abierta, setAbierta] = useState<string | null>(null);
    const [intento, setIntento] = useState(0);
    const [ventana, setVentana] = useState(false);
    const [origen, setOrigen] = useState('');
    const [exportacion, setExportacion] = useState<Exportacion | null>(null);
    const [ocupado, setOcupado] = useState(false);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        const { desde, hasta } = rango(mes, new Date());
        ops.getPaymentsSummary(negocio, desde, hasta, true)
            .then((r) => {
                if (vivo) {
                    setResumen({ mes, datos: r, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setResumen({ mes, datos: null, error: e });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, mes, intento]);

    useEffect(() => {
        if (!detalle) {
            return;
        }
        let vivo = true;
        const { desde, hasta } = rango(mes, new Date());
        const vista = `${mes}|${pag.cursor ?? ''}|${String(intento)}`;
        ops.listPayouts(negocio, { arrival_from: desde, arrival_to: hasta, cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setTransferencias({ vista, lista: l });
                }
            })
            .catch(() => {
                if (vivo) {
                    setTransferencias({ vista, lista: { data: [], next_cursor: null } });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, detalle, mes, pag.cursor, intento]);

    // La exportación se prepara en segundo plano: se mira cada 2 s hasta que está lista.
    const idExportacion = exportacion !== null && exportacion.status === 'preparing' ? exportacion.id : null;
    useEffect(() => {
        if (idExportacion === null) {
            return;
        }
        const reloj = setInterval(() => {
            ops.getPaymentExport(negocio, idExportacion)
                .then((x) => {
                    if (x.status !== 'preparing') {
                        setExportacion(x);
                    }
                })
                .catch(() => undefined);
        }, 2000);
        return () => {
            clearInterval(reloj);
        };
    }, [ops, negocio, idExportacion]);

    const acciones: AccionCabecera[] = [];
    if (ver && exportar) {
        acciones.push({ clave: 'exportar', texto: 'Exportar CSV', icono: 'download', onClick: () => { setExportacion(null); setOrigen(''); setVentana(true); } });
    }
    useCabecera({ ayuda: ver ? AYUDA.conciliacion : [], acciones });

    if (!ver) {
        return <EstadoSinPermiso seccion="Conciliación" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    const r = resumen?.mes === mes ? resumen : null;
    const transferencias = transferenciasLeidas?.vista === `${mes}|${pag.cursor ?? ''}|${String(intento)}` ? transferenciasLeidas.lista : null;
    const contexto = (
        <div className="rv-pg-contexto">
            <Contexto etiqueta="Periodo" icono="calendar" valor={mes} opciones={opciones} onChange={(v) => { setMes(v); reiniciar(); }} movil={movil} />
        </div>
    );
    if (r?.error != null && r.datos === null) {
        return (
            <div className="rv-ca-apartado">
                {contexto}
                <EstadoError seccion="la conciliación" codigoSoporte={estadoDeError(r.error).codigoSoporte} onReintentar={recargar} movil={movil} />
            </div>
        );
    }
    if (r === null || r.datos === null) {
        return (
            <div className="rv-ca-apartado">
                {contexto}
                <EstadoCargando seccion="la conciliación" />
            </div>
        );
    }
    const s = r.datos;
    const prev = s.previous ?? null;
    const frente = (valor: (x: NonNullable<Resumen['previous']>) => { amount: number; currency: string }) => (prev === null ? undefined : `frente a ${dinero(valor(prev))} del ${tramo(prev)}`);
    const moneda = simboloMoneda(s.currency);
    const vacio = s.payment_count === 0 && s.collected.amount === 0;
    const irA = (apartado: string) => () => { navegar(`${ruta}?apartado=${apartado}`); };
    const maximo = (filas: readonly { collected: { amount: number } }[]) => Math.max(1, ...filas.map((f) => f.collected.amount));
    const porForma = s.by_method ?? [];
    const porConcepto = s.by_concept ?? [];

    return (
        <div className="rv-ca-apartado">
            {contexto}
            <p className="rv-ca-nota rv-ca-nota--gris">{`En la moneda de tu negocio (${s.currency.toUpperCase()}). Los pagos en el local también cuentan.`}</p>
            {!detalle && <p className="rv-ca-nota"><Icono nombre="eye" /><span>Con tu rol ves las cifras del periodo, sin el detalle de cada pago.</span></p>}
            {vacio ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre="landmark" /></span>
                    <strong>Sin movimientos en este periodo</strong>
                    <span>Cuando cobres online o registres pagos en el local, aquí verás lo cobrado, lo devuelto y las comisiones.</span>
                </div>
            ) : (
                <>
                    <div className={`rv-pg-cifras${movil ? ' rv-pg-cifras--movil' : ''}`}>
                        <TarjetaCifra oscura titulo="Cobrado" cifra={cifraDinero(s.collected)} moneda={moneda} pie={[`${s.payment_count.toLocaleString('es-ES')} pagos`, frente((x) => x.collected)].filter((x) => x !== undefined).join(' · ')} enlace={detalle ? { texto: 'Ver pagos', accion: irA('pagos') } : undefined} />
                        <TarjetaCifra titulo="Devuelto" cifra={cifraDinero(s.refunded)} moneda={moneda} pie={frente((x) => x.refunded)} enlace={detalle ? { texto: 'Ver pagos', accion: irA('pagos') } : undefined} />
                        <TarjetaCifra titulo="En disputa" cifra={cifraDinero(s.disputed)} moneda={moneda} pie={frente((x) => x.disputed)} enlace={detalle ? { texto: 'Ver disputas', accion: irA('disputas') } : undefined} />
                        <TarjetaCifra titulo="Comisiones" cifra={cifraDinero(s.stripe_fees ?? { amount: 0, currency: s.currency })} moneda={`${moneda} de Stripe`} pie={`${dinero(s.platform_fees)} de plataforma`} />
                        <TarjetaCifra titulo="Neto" cifra={cifraDinero(s.net)} moneda={moneda} pie={['Lo que te queda', frente((x) => x.net)].filter((x) => x !== undefined).join(' · ')} />
                    </div>
                    {(porForma.length > 0 || porConcepto.length > 0) && (
                        <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                            <section className="rv-pg-tarjeta" aria-label="Por forma de pago">
                                <strong>Por forma de pago</strong>
                                {porForma.map((f) => <Barra key={f.method} texto={FORMA_PAGO[f.method][0]} valor={dinero(f.collected)} parte={f.collected.amount / maximo(porForma)} />)}
                            </section>
                            <section className="rv-pg-tarjeta" aria-label="Por concepto">
                                <strong>Por concepto</strong>
                                {porConcepto.map((f) => <Barra key={f.concept} texto={CONCEPTO[f.concept]} valor={dinero(f.collected)} parte={f.collected.amount / maximo(porConcepto)} />)}
                            </section>
                        </div>
                    )}
                </>
            )}
            {detalle && (
                <section className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Transferencias al banco</p>
                    {transferencias === null ? (
                        <EstadoCargando seccion="las transferencias" />
                    ) : transferencias.data.length === 0 ? (
                        <span className="rv-ca-nota rv-ca-nota--gris">Ninguna transferencia en este periodo.</span>
                    ) : (
                        <>
                            <div className="rv-ca-lista" role="list" aria-label="Transferencias al banco">
                                {transferencias.data.map((t) => (
                                    <div key={t.id} role="listitem" tabIndex={0} className="rv-ca-fila rv-ca-fila--abre" aria-label={`Transferencia del ${diaMes(t.arrival_date)}, ${dinero(t.amount)}. Abrir`} onClick={() => { setAbierta(t.id); }} onKeyDown={(e) => { if (e.key === 'Enter') { setAbierta(t.id); } }}>
                                        <span className="rv-ca-baldosa"><Icono nombre="landmark" /></span>
                                        <span className="rv-ca-fila__texto">
                                            <strong>{`Llega el ${diaMes(t.arrival_date)}`}</strong>
                                            <span>{t.item_count === 1 ? '1 movimiento' : `${t.item_count.toLocaleString('es-ES')} movimientos`}{t.failure_label == null ? '' : ` · ${t.failure_label}`}</span>
                                        </span>
                                        {(t.status === 'failed' || t.status === 'canceled') && <Chip estado={ESTADO_TRANSFERENCIA[t.status][1]}>{ESTADO_TRANSFERENCIA[t.status][0]}</Chip>}
                                        <strong className="rv-pg-importe">{dinero(t.amount)}</strong>
                                        <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                    </div>
                                ))}
                            </div>
                            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(transferencias.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(transferencias.next_cursor); }} movil={movil} />
                        </>
                    )}
                </section>
            )}
            <FichaTransferenciaPanel ops={ops} negocio={negocio} movil={movil} id={abierta} onCerrar={() => { setAbierta(null); }} />
            <Ventana
                abierta={ventana}
                titulo={exportacion?.status === 'ready' ? 'Tu archivo está listo' : 'Exportar CSV'}
                texto={exportacion?.status === 'ready' ? `Caduca${exportacion.expires_at == null ? ' pronto' : ` el ${diaMes(exportacion.expires_at)}`}. Descárgalo antes.` : 'Pagos, devoluciones, disputas y comisiones del periodo, con las referencias de Stripe.'}
                accion={exportacion?.status === 'ready' ? 'Descargar' : 'Exportar'}
                accionIcono="download"
                ocupado={ocupado || exportacion?.status === 'preparing'}
                ocupadoTexto="Preparando…"
                inactiva={exportacion?.status === 'failed' || exportacion?.status === 'expired'}
                movil={movil}
                onAccion={() => {
                    if (exportacion?.status === 'ready' && exportacion.download_url != null) {
                        navegador.abrir(exportacion.download_url);
                        setVentana(false);
                        return;
                    }
                    const { desde, hasta } = rango(mes, new Date());
                    setOcupado(true);
                    ops.createPaymentExport(negocio, { from: desde, to: hasta, ...(origen === '' ? {} : { origin: origen as 'stripe' | 'external' }) })
                        .then(setExportacion)
                        .catch((e: unknown) => {
                            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido preparar el archivo. Prueba de nuevo.') });
                        })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setVentana(false); }}
            >
                {exportacion === null && (
                    <div className="rv-ca-bloque">
                        <div className="rv-ca-ventana-linea"><span>Periodo</span><strong>{opciones.find((o) => o.valor === mes)?.texto ?? mes}</strong></div>
                        <Desplegable etiqueta="Qué pagos" valor={origen} opciones={[{ valor: '', texto: 'Todos' }, { valor: 'stripe', texto: 'Solo los online, con Stripe' }, { valor: 'external', texto: 'Solo los del local' }]} onChange={setOrigen} />
                    </div>
                )}
                {exportacion?.status === 'failed' && <p className="rv-ca-nota"><Icono nombre="circle-alert" /><span>No se ha podido preparar. Ciérralo y prueba de nuevo.</span></p>}
            </Ventana>
        </div>
    );
}

function Barra({ texto, valor, parte }: { texto: string; valor: string; parte: number }) {
    return (
        <div className="rv-pg-barra-h">
            <span className="rv-pg-barra-h__texto"><span>{texto}</span><strong>{valor}</strong></span>
            <span className="rv-pg-barra-h__fondo" aria-hidden="true"><span style={{ width: `${String(Math.max(2, Math.round(parte * 100)))}%` }} /></span>
        </div>
    );
}

const TIPO_MOVIMIENTO: Readonly<Record<FichaTransferencia['items'][number]['kind'], string>> = {
    charge: 'Cobro',
    refund: 'Reembolso',
    dispute: 'Disputa',
    dispute_reversal: 'Disputa ganada',
    platform_fee: 'Comisión de plataforma',
    stripe_fee: 'Comisión de Stripe',
    adjustment: 'Ajuste',
};

function FichaTransferenciaPanel({ ops, negocio, movil, id, onCerrar }: { ops: PropsPantalla['ops']; negocio: string; movil: boolean; id: string | null; onCerrar: () => void }) {
    const [leida, setFicha] = useState<{ id: string; cursor: string | null; ficha: FichaTransferencia } | null>(null);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [idAntes, setIdAntes] = useState(id);
    if (id !== idAntes) {
        setIdAntes(id);
        reiniciar();
    }
    useEffect(() => {
        if (id === null) {
            return;
        }
        let vivo = true;
        const cursor = pag.cursor;
        ops.getPayout(negocio, id, { cursor })
            .then((f) => {
                if (vivo) {
                    setFicha({ id, cursor, ficha: f });
                }
            })
            .catch(() => {
                if (vivo) {
                    onCerrar();
                }
            });
        return () => {
            vivo = false;
        };
        // onCerrar cambia en cada pintado; solo importa la transferencia y la página.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, id, pag.cursor]);
    const ficha = leida !== null && leida.id === id && leida.cursor === pag.cursor ? leida.ficha : null;
    const t = ficha?.payout;
    return (
        <PanelLateral abierto={id !== null} onCerrar={onCerrar} movil={movil} tamano="ficha" titulo={t === undefined ? 'Transferencia' : dinero(t.amount)} eyebrow="Transferencia al banco" subtitulo={t === undefined ? undefined : `Llega el ${diaMes(t.arrival_date)}`} icono="landmark">
            {ficha === null ? (
                <EstadoCargando seccion="la transferencia" />
            ) : (
                <div className="rv-ca-bloque" role="list" aria-label="Movimientos de la transferencia">
                    {ficha.items.map((m, i) => (
                        <div key={`${m.kind}${m.payment_id ?? m.refund_id ?? m.dispute_id ?? ''}${String(i)}`} role="listitem" className="rv-pg-movimiento">
                            <span className="rv-ca-fila__texto"><strong>{TIPO_MOVIMIENTO[m.kind]}</strong><span>{`Bruto ${dinero(m.gross)} · comisión ${dinero(m.fee)}`}</span></span>
                            <strong className="rv-pg-importe">{dinero(m.net)}</strong>
                        </div>
                    ))}
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(ficha.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(ficha.next_cursor); }} movil={movil} />
                </div>
            )}
        </PanelLateral>
    );
}
